/**
 * M76 — route attachment + encounter patterns for multi-route production maps.
 *
 * Patterns (ground + campaign AIR on physical lanes):
 * - alternating: A, B, A, B…
 * - simultaneous: when a wave has 2+ ground groups, assign distinct lanes
 * - primary-heavy: ~2/3 A, 1/3 other (fallback)
 * - round-robin: A, B, C… (triple)
 *
 * Campaign V3: AIR binds to physical GROUND lanes (A/B/C), never air-a shortcuts.
 * Dedicated air-* routes remain for explicit DEV / special maps only.
 */

import type { StageDefinition, StageWaveGroupSpec, StageWaveSpec } from '../combat/campaign/types'
import { getMap } from '../model/mapCatalog'
import { normalizeMapRoutes } from '../model/path'
import { ENEMY_CATALOG, enemyMovementClass } from '../model/enemies'
import { CHAPTER2_MAP_OVERRIDES } from './chapter2Maps'
import { isDedicatedAirRouteId } from './airNavigation'

export type EncounterPatternId =
  | 'primary-only'
  | 'alternating'
  | 'simultaneous-pairs'
  | 'primary-heavy'
  | 'round-robin'

function groundRouteIds(mapId: string): string[] {
  const map = getMap(mapId)
  if (!map) return ['ground-a']
  return normalizeMapRoutes(map)
    .filter((r) => r.movementClass === 'GROUND')
    .map((r) => r.id)
}

function stageOrdinal(stage: StageDefinition): number {
  return Number(stage.displayLabel.split('-')[1] ?? 0)
}

/** Which encounter pattern a stage should use (docs + tests). */
export function encounterPatternForStage(stage: StageDefinition): EncounterPatternId {
  if (stage.chapterId === 'chapter-1') {
    // Ch1 multi-route stages author routeIds explicitly.
    return 'primary-only'
  }
  const n = stageOrdinal(stage)
  if (CHAPTER2_MAP_OVERRIDES[n] === undefined) return 'primary-only'
  if (stage.mapId === 'triad') return 'primary-heavy'
  if (stage.mapId === 'delta') return 'primary-heavy'
  // Twinspire / skew / confluence: prefer primary-heavy so Normal bots can focus one lane
  // while secondary pressure still teaches multi-route. Alternating is too punishing on long levels.
  if (
    stage.mapId === 'twinspire' ||
    stage.mapId === 'skew' ||
    stage.mapId === 'confluence'
  ) {
    return 'primary-heavy'
  }
  return 'primary-heavy'
}

function assignGround(
  pattern: EncounterPatternId,
  groundIds: string[],
  cursor: { i: number },
  waveGroundIndex: number
): string {
  if (groundIds.length <= 1) return groundIds[0]!
  if (pattern === 'primary-only') return groundIds[0]!

  if (pattern === 'round-robin') {
    const id = groundIds[cursor.i % groundIds.length]!
    cursor.i += 1
    return id
  }

  if (pattern === 'alternating') {
    const id = groundIds[cursor.i % groundIds.length]!
    cursor.i += 1
    return id
  }

  if (pattern === 'simultaneous-pairs') {
    // Within a wave, successive ground groups get distinct lanes first.
    const id = groundIds[waveGroundIndex % groundIds.length]!
    cursor.i += 1
    return id
  }

  // primary-heavy
  const n = cursor.i
  cursor.i += 1
  if (n % 3 === 2) {
    const secondaryIndex = 1 + (Math.floor(n / 3) % (groundIds.length - 1))
    return groundIds[secondaryIndex]!
  }
  return groundIds[0]!
}

function assignGroupRoute(
  group: StageWaveGroupSpec,
  groundIds: string[],
  laneCursor: { i: number },
  pattern: EncounterPatternId,
  waveGroundIndex: { n: number }
): StageWaveGroupSpec {
  const isAir = enemyMovementClass(ENEMY_CATALOG[group.enemyId]) === 'AIR'
  const legacyAirShortcut = isDedicatedAirRouteId(group.routeId)

  // Preserve explicit physical-lane (or DEV air) bindings except legacy campaign air-* shortcuts.
  if (group.routeId && !legacyAirShortcut) return group

  if (isAir || legacyAirShortcut) {
    if (groundIds.length === 0) return { ...group, routeId: undefined }
    const routeId = assignGround(pattern, groundIds, laneCursor, waveGroundIndex.n)
    waveGroundIndex.n += 1
    return { ...group, routeId }
  }

  if (groundIds.length === 0) return group
  const routeId = assignGround(pattern, groundIds, laneCursor, waveGroundIndex.n)
  waveGroundIndex.n += 1
  return { ...group, routeId }
}

/** Attach routeIds using stage encounter pattern. */
export function withDistributedRoutes(stage: StageDefinition): StageDefinition {
  const groundIds = groundRouteIds(stage.mapId)
  if (groundIds.length === 0) return stage

  const pattern = encounterPatternForStage(stage)
  const cursor = { i: 0 }
  const waves: StageWaveSpec[] = stage.waves.map((wave) => {
    const waveGroundIndex = { n: 0 }
    return {
      ...wave,
      groups: wave.groups.map((g) =>
        assignGroupRoute(g, groundIds, cursor, pattern, waveGroundIndex)
      )
    }
  })
  return { ...stage, waves }
}

export function withDistributedRoutesAll(stages: StageDefinition[]): StageDefinition[] {
  return stages.map(withDistributedRoutes)
}
